import { isValidApiBaseProps } from '@common/api.services';
import { FETCH_QUERIES_NAME, MUTATION_KEYS } from '@common/constants';
import { AxiosErrorResponse } from '@core/error.models';
import { isPositiveNumber } from '@core/Primitives/number.services';
import { IItemResponse } from '@gcl/react-core';
import { useMutation, useQuery } from '@tanstack/react-query';
import { IProjectTwcGeRequest, IUpsertProjectTwcGeRequestRequest } from './projectTwcGeRequest.models';
import {
  approveProjectTwcGeRequestByEmAsync,
  approveProjectTwcGeRequestByHoeAsync,
  approveProjectTwcGeRequestByPmAsync,
  cancelProjectTwcGeRequestAsync,
  getAllProjectTwcGeRequestsAsync,
  getProjectTwcGeRequestByIdAsync,
  IGetAllProjectTwcGeRequestProps,
  IGetProjectTwcRequestByIdAsyncProps,
  IProjectTwcGeRequestStatusChangeProps,
  issueProjectTwcGeRequestToPmAsync,
  revokeProjectTwcGeRequestAsync,
  saveProjectTwcGeRequestAsDraftAsync,
} from './projectTwcGeRequest.service';

export const useFetchAllProjectTwcGeRequests = (props: IGetAllProjectTwcGeRequestProps) => {
  return useQuery<IProjectTwcGeRequest[], AxiosErrorResponse>(
    [props.projectId, FETCH_QUERIES_NAME.PROJECT_TWC_GE_REQUESTS, props.applicationType],
    async () => {
      const results = await getAllProjectTwcGeRequestsAsync(props);
      return results?.items || [];
    },
    {
      enabled: isValidApiBaseProps(props),
    },
  );
};

export const useFetchProjectTwcGeRequestById = (props: IGetProjectTwcRequestByIdAsyncProps, enabled: boolean) => {
  return useQuery<IProjectTwcGeRequest | undefined, AxiosErrorResponse>(
    [props.projectId, FETCH_QUERIES_NAME.PROJECT_TWC_GE_REQUEST_BY_ID, props.id],
    async () => {
      const results = await getProjectTwcGeRequestByIdAsync(props);
      return results?.item;
    },
    {
      enabled: isValidApiBaseProps(props) && isPositiveNumber(props.id) && enabled,
    },
  );
};

export const useSaveProjectTwcGeRequestAsDraft = () => {
  return useMutation<IItemResponse<IProjectTwcGeRequest>, AxiosErrorResponse, any, unknown>(
    [MUTATION_KEYS.SAVE_PROJECT_TWC_GE_REQUEST_AS_DRAFT],
    async (request: IUpsertProjectTwcGeRequestRequest) => {
      const result = await saveProjectTwcGeRequestAsDraftAsync(request);
      return result;
    },
  );
};

export const useIssueProjectTwcGeRequestToPm = () => {
  return useMutation<IItemResponse<IProjectTwcGeRequest>, AxiosErrorResponse, any, unknown>(
    [MUTATION_KEYS.ISSUE_PROJECT_TWC_GE_REQUEST_TO_PM],
    async (request: IUpsertProjectTwcGeRequestRequest) => {
      const result = await issueProjectTwcGeRequestToPmAsync(request);
      return result;
    },
  );
};

export const useApproveProjectTwcGeRequestByPm = () => {
  return useMutation<IItemResponse<IProjectTwcGeRequest>, AxiosErrorResponse, any, unknown>(
    [MUTATION_KEYS.APPROVE_PROJECT_TWC_GE_REQUEST_BY_PM],
    async (request: IUpsertProjectTwcGeRequestRequest) => {
      const result = await approveProjectTwcGeRequestByPmAsync(request);
      return result;
    },
  );
};

export const useApproveProjectTwcGeRequestByEm = () => {
  return useMutation<IItemResponse<IProjectTwcGeRequest>, AxiosErrorResponse, any, unknown>(
    [MUTATION_KEYS.APPROVE_PROJECT_TWC_GE_REQUEST_BY_EM],
    async (request: IUpsertProjectTwcGeRequestRequest) => {
      const result = await approveProjectTwcGeRequestByEmAsync(request);
      return result;
    },
  );
};

export const useApproveProjectTwcGeRequestByHoe = () => {
  return useMutation<IItemResponse<IProjectTwcGeRequest>, AxiosErrorResponse, any, unknown>(
    [MUTATION_KEYS.APPROVE_PROJECT_TWC_GE_REQUEST_BY_HOE],
    async (request: IUpsertProjectTwcGeRequestRequest) => {
      const result = await approveProjectTwcGeRequestByHoeAsync(request);
      return result;
    },
  );
};

export const useCancelProjectTwcGeRequest = () => {
  return useMutation<IItemResponse<IProjectTwcGeRequest>, AxiosErrorResponse, any, unknown>(
    [MUTATION_KEYS.CANCEL_PROJECT_TWC_GE_REQUEST],
    async (request: IProjectTwcGeRequestStatusChangeProps) => {
      const result = await cancelProjectTwcGeRequestAsync(request);
      return result;
    },
  );
};

export const useRevokeProjectTwcGeRequest = () => {
  return useMutation<IItemResponse<IProjectTwcGeRequest>, AxiosErrorResponse, any, unknown>(
    [MUTATION_KEYS.REVOKE_PROJECT_TWC_GE_REQUEST],
    async (request: IProjectTwcGeRequestStatusChangeProps) => {
      const result = await revokeProjectTwcGeRequestAsync(request);
      return result;
    },
  );
};
